// src/pages/admin/BulkInsertMaster.jsx
import React, { useMemo, useState, useContext } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import HeaderBar from "../components/HeaderBar";
import AdminLayoutWrapper from "../components/AdminLayoutWrapper";
import getCurrentSkin from "../utils/getCurrentSkin";
import { ThemeContext } from "../context/ThemeContext";

import {
  FaCloudUploadAlt,
  FaCheckCircle,
  FaSave,
  FaDownload,
  FaListUl,
} from "react-icons/fa";

/**
 * FULL React page for your Bulk Insert Master (matches the Node routes I gave)
 *
 * Endpoints used (BASE_URL):
 *  - POST /api/admin/bulk/bulk-items/upload   (multipart form file)
 *  - POST /api/admin/bulk/bulk-items/save    (json {type})
 *
 *  - POST /api/admin/bulk/supplier/check     (multipart)
 *  - POST /api/admin/bulk/supplier/save      (multipart + userId)
 *
 *  - POST /api/admin/bulk/veh-battery/check  (multipart)
 *  - POST /api/admin/bulk/veh-battery/save   (multipart)
 *
 *  - POST /api/admin/bulk/veh-tyre/check     (multipart + mode)
 *  - POST /api/admin/bulk/veh-tyre/save      (multipart + mode + userId)
 *
 *  - POST /api/admin/bulk/asset/check        (multipart + assetType)
 *  - POST /api/admin/bulk/asset/save         (multipart + assetType + userId)
 *
 *  - POST /api/admin/bulk/vehicle/check      (multipart + mode)
 *  - POST /api/admin/bulk/vehicle/save       (multipart + mode + userId)
 *
 *  - POST /api/admin/bulk/payroll-employee/check (multipart + financialYear)
 *  - POST /api/admin/bulk/payroll-employee/save  (multipart + financialYear)
 *
 *  - GET  /api/admin/bulk/templates/:name
 */

const BASE_URL = "https://nissi-india.com"; // change to env if needed

function getFinYearId() {
  try {
    const direct = localStorage.getItem("finYearId");
    if (direct) return String(direct);
    const admin = JSON.parse(localStorage.getItem("admin") || "null");
    if (admin?.finYearId) return String(admin.finYearId);
  } catch (_) {}
  return "";
}

function tenantHeaders() {
  const finYearId = getFinYearId();
  return finYearId ? { "x-fin-year-id": finYearId } : {};
}

/* ----------------------------- Small UI helpers ---------------------------- */

const TabButton = ({ active, onClick, icon, title, desc, isDark }) => {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left rounded-xl border p-4 transition ${
        isDark
          ? active
            ? "border-white/30 bg-white/10 text-white shadow-sm"
            : "border-white/10 bg-transparent text-white hover:bg-white/5"
          : active
            ? "border-black bg-white text-gray-900 shadow-sm"
            : "border-gray-200 bg-white text-gray-900 hover:shadow-sm"
      }`}
    >
      <div className="flex items-start gap-3">
        <div className="text-xl mt-1">{icon}</div>
        <div className="flex-1">
          <div className="font-semibold">{title}</div>
          <div className={`text-sm mt-1 ${isDark ? "text-white/60" : "text-gray-500"}`}>
            {desc}
          </div>
        </div>
      </div>
    </button>
  );
};

const SectionCard = ({ title, children, right }) => {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white text-gray-900 shadow-sm">
      <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between gap-3">
        <div className="font-semibold text-gray-900">{title}</div>
        <div>{right}</div>
      </div>
      <div className="p-5 text-gray-900">{children}</div>
    </div>
  );
};

const FilePicker = ({ file, setFile, accept = ".xls,.xlsx" }) => {
  return (
    <div className="rounded-xl border border-gray-200 p-4 bg-gray-50 text-gray-900">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="font-medium text-gray-900">Upload Excel</div>
          <div className="text-sm text-gray-500">Accepted: {accept}</div>
        </div>
        <label className="inline-flex items-center gap-2 cursor-pointer rounded-xl border border-gray-300 px-4 py-2 bg-white text-gray-800 hover:bg-gray-50">
          <FaCloudUploadAlt />
          <span>{file ? "Change file" : "Choose file"}</span>
          <input
            type="file"
            accept={accept}
            className="hidden"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
          />
        </label>
      </div>
      {file && (
        <div className="mt-3 text-sm text-gray-800">
          <span className="font-medium">Selected:</span> {file.name}{" "}
          <span className="text-gray-500">({Math.round(file.size / 1024)} KB)</span>
        </div>
      )}
    </div>
  );
};

const controlClass =
  "mt-3 w-full rounded-xl border border-gray-300 bg-white text-gray-900 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-gray-300";

function useButtonClasses() {
  const { skin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  // If you already use tailwind dynamic classes in your app, keep it.
  // Otherwise, replace with a fixed class like "bg-indigo-600".
  const primary = `bg-${baseColor}-${baseShade} hover:bg-${baseColor}-700`;
  return { primary };
}

async function postMultipart(url, dataObj, onUploadProgress) {
  const form = new FormData();
  Object.entries(dataObj).forEach(([k, v]) => {
    if (v !== undefined && v !== null) form.append(k, v);
  });

  const res = await axios.post(url, form, {
    headers: { "Content-Type": "multipart/form-data", ...tenantHeaders() },
    onUploadProgress,
  });
  return res.data;
}

async function postJson(url, payload) {
  const res = await axios.post(url, payload, { headers: tenantHeaders() });
  return res.data;
}

function downloadTemplate(name) {
  window.open(`${BASE_URL}/api/admin/bulk/templates/${name}`, "_blank");
}

/* ------------------------------- Main component ---------------------------- */

const BulkInsertMaster = () => {
  const { isDark } = useContext(ThemeContext);
  const { primary } = useButtonClasses();

  const [active, setActive] = useState("items");

  // shared
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);

  // Items
  const [itemsFile, setItemsFile] = useState(null);
  const [itemSaveType, setItemSaveType] = useState("Category");

  // Supplier
  const [supplierFile, setSupplierFile] = useState(null);

  // Vehicle Battery
  const [batteryFile, setBatteryFile] = useState(null);

  // Tyre
  const [tyreFile, setTyreFile] = useState(null);
  const [tyreMode, setTyreMode] = useState("NewTyre"); // or Retreded

  // Asset
  const [assetFile, setAssetFile] = useState(null);
  const [assetType, setAssetType] = useState("Others"); // Others | Land | SMaterials

  // Vehicle
  const [vehicleFile, setVehicleFile] = useState(null);
  const [vehicleMode, setVehicleMode] = useState("New"); // New | Hire

  // Payroll
  const [payrollFile, setPayrollFile] = useState(null);
  const [financialYear, setFinancialYear] = useState("");

  // userId: set from your auth/session; for now read from localStorage
  const userId = useMemo(() => {
    const v = localStorage.getItem("AdminID") || localStorage.getItem("adminId") || "0";
    return Number(v || 0);
  }, []);

  const tabs = [
    {
      key: "items",
      title: "Items Bulk",
      desc: "Upload items list to staging table then Save by type",
      icon: <FaListUl />,
    },
    {
      key: "supplier",
      title: "Supplier Bulk",
      desc: "Check supplier excel then Save to master",
      icon: <FaListUl />,
    },
    {
      key: "vehicle",
      title: "Vehicle Bulk",
      desc: "New / Hire vehicle bulk check & save",
      icon: <FaListUl />,
    },
    {
      key: "tyre",
      title: "Vehicle Tyre Bulk",
      desc: "New Tyre / Retreaded tyre bulk check & save",
      icon: <FaListUl />,
    },
    {
      key: "battery",
      title: "Vehicle Battery Bulk",
      desc: "Battery card bulk check & save",
      icon: <FaListUl />,
    },
    {
      key: "asset",
      title: "Asset Bulk",
      desc: "Others / Land / SMaterials bulk check & save",
      icon: <FaListUl />,
    },
    {
      key: "payroll",
      title: "Payroll Employee Bulk",
      desc: "Multi-sheet employee bulk (financial year based)",
      icon: <FaListUl />,
    },
  ];

  const handleProgress = (p) => setProgress(p);

  const withBusy = async (fn) => {
    try {
      setBusy(true);
      setProgress(0);
      await fn();
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || "Failed";
      toast.error(msg);
    } finally {
      setBusy(false);
      setProgress(0);
    }
  };

  /* --------------------------------- ITEMS -------------------------------- */

  const itemsUpload = () =>
    withBusy(async () => {
      if (!itemsFile) return toast.error("Please choose file");
      const data = await postMultipart(
        `${BASE_URL}/api/admin/bulk/bulk-items/upload`,
        { file: itemsFile },
        (evt) => {
          if (!evt.total) return;
          handleProgress(Math.round((evt.loaded * 100) / evt.total));
        }
      );
      if (data.ok) toast.success(data.message || "Uploaded");
      else toast.error(data.message || "Upload failed");
    });

  const itemsSave = () =>
    withBusy(async () => {
      const data = await postJson(`${BASE_URL}/api/admin/bulk/bulk-items/save`, {
        type: itemSaveType,
      });
      if (data.ok) toast.success(data.message || "Saved");
      else toast.error(data.message || "Save failed");
    });

  /* ------------------------------- SUPPLIER -------------------------------- */

  const supplierCheck = () =>
    withBusy(async () => {
      if (!supplierFile) return toast.error("Please choose file");
      const data = await postMultipart(
        `${BASE_URL}/api/admin/bulk/supplier/check`,
        { file: supplierFile },
        (evt) => evt.total && handleProgress(Math.round((evt.loaded * 100) / evt.total))
      );
      data.ok ? toast.success(data.message) : toast.error(data.message);
    });

  const supplierSave = () =>
    withBusy(async () => {
      if (!supplierFile) return toast.error("Please choose file");
      const data = await postMultipart(
        `${BASE_URL}/api/admin/bulk/supplier/save`,
        { file: supplierFile, userId },
        (evt) => evt.total && handleProgress(Math.round((evt.loaded * 100) / evt.total))
      );
      data.ok ? toast.success(data.message) : toast.error(data.message);
    });

  /* -------------------------------- VEHICLE -------------------------------- */

  const vehicleCheck = () =>
    withBusy(async () => {
      if (!vehicleFile) return toast.error("Please choose file");
      const data = await postMultipart(
        `${BASE_URL}/api/admin/bulk/vehicle/check`,
        { file: vehicleFile, mode: vehicleMode },
        (evt) => evt.total && handleProgress(Math.round((evt.loaded * 100) / evt.total))
      );
      data.ok ? toast.success(data.message) : toast.error(data.message);
    });

  const vehicleSave = () =>
    withBusy(async () => {
      if (!vehicleFile) return toast.error("Please choose file");
      const data = await postMultipart(
        `${BASE_URL}/api/admin/bulk/vehicle/save`,
        { file: vehicleFile, mode: vehicleMode, userId },
        (evt) => evt.total && handleProgress(Math.round((evt.loaded * 100) / evt.total))
      );
      data.ok ? toast.success(data.message) : toast.error(data.message);
    });

  /* --------------------------------- TYRE --------------------------------- */

  const tyreCheck = () =>
    withBusy(async () => {
      if (!tyreFile) return toast.error("Please choose file");
      const data = await postMultipart(
        `${BASE_URL}/api/admin/bulk/veh-tyre/check`,
        { file: tyreFile, mode: tyreMode },
        (evt) => evt.total && handleProgress(Math.round((evt.loaded * 100) / evt.total))
      );
      data.ok ? toast.success(data.message) : toast.error(data.message);
    });

  const tyreSave = () =>
    withBusy(async () => {
      if (!tyreFile) return toast.error("Please choose file");
      const data = await postMultipart(
        `${BASE_URL}/api/admin/bulk/veh-tyre/save`,
        { file: tyreFile, mode: tyreMode, userId },
        (evt) => evt.total && handleProgress(Math.round((evt.loaded * 100) / evt.total))
      );
      data.ok ? toast.success(data.message) : toast.error(data.message);
    });

  /* -------------------------------- BATTERY -------------------------------- */

  const batteryCheck = () =>
    withBusy(async () => {
      if (!batteryFile) return toast.error("Please choose file");
      const data = await postMultipart(
        `${BASE_URL}/api/admin/bulk/veh-battery/check`,
        { file: batteryFile },
        (evt) => evt.total && handleProgress(Math.round((evt.loaded * 100) / evt.total))
      );
      data.ok ? toast.success(data.message) : toast.error(data.message);
    });

  const batterySave = () =>
    withBusy(async () => {
      if (!batteryFile) return toast.error("Please choose file");
      const data = await postMultipart(
        `${BASE_URL}/api/admin/bulk/veh-battery/save`,
        { file: batteryFile },
        (evt) => evt.total && handleProgress(Math.round((evt.loaded * 100) / evt.total))
      );
      data.ok ? toast.success(data.message) : toast.error(data.message);
    });

  /* --------------------------------- ASSET -------------------------------- */

  const assetCheck = () =>
    withBusy(async () => {
      if (!assetFile) return toast.error("Please choose file");
      const data = await postMultipart(
        `${BASE_URL}/api/admin/bulk/asset/check`,
        { file: assetFile, assetType },
        (evt) => evt.total && handleProgress(Math.round((evt.loaded * 100) / evt.total))
      );
      data.ok ? toast.success(data.message) : toast.error(data.message);
    });

  const assetSave = () =>
    withBusy(async () => {
      if (!assetFile) return toast.error("Please choose file");
      const data = await postMultipart(
        `${BASE_URL}/api/admin/bulk/asset/save`,
        { file: assetFile, assetType, userId },
        (evt) => evt.total && handleProgress(Math.round((evt.loaded * 100) / evt.total))
      );
      data.ok ? toast.success(data.message) : toast.error(data.message);
    });

  /* ------------------------------ PAYROLL EMP ------------------------------ */

  const payrollCheck = () =>
    withBusy(async () => {
      if (!payrollFile) return toast.error("Please choose file");
      if (!financialYear) return toast.error("Please enter financial year");
      const data = await postMultipart(
        `${BASE_URL}/api/admin/bulk/payroll-employee/check`,
        { file: payrollFile, financialYear },
        (evt) => evt.total && handleProgress(Math.round((evt.loaded * 100) / evt.total))
      );
      data.ok ? toast.success(data.message) : toast.error(data.message);
    });

  const payrollSave = () =>
    withBusy(async () => {
      if (!payrollFile) return toast.error("Please choose file");
      if (!financialYear) return toast.error("Please enter financial year");
      const data = await postMultipart(
        `${BASE_URL}/api/admin/bulk/payroll-employee/save`,
        { file: payrollFile, financialYear },
        (evt) => evt.total && handleProgress(Math.round((evt.loaded * 100) / evt.total))
      );
      data.ok ? toast.success(data.message) : toast.error(data.message);
    });

  /* --------------------------------- Render -------------------------------- */

  const ActionButtons = ({ onCheck, onSave, onUploadOnly, templateName }) => {
    return (
      <div className="flex gap-2 flex-wrap">
        {templateName && (
          <button
            type="button"
            onClick={() => downloadTemplate(templateName)}
            className="inline-flex items-center gap-2 rounded-xl border px-4 py-2 hover:bg-gray-50"
          >
            <FaDownload />
            Template
          </button>
        )}

        {onUploadOnly && (
          <button
            type="button"
            onClick={onUploadOnly}
            disabled={busy}
            className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-white ${primary} disabled:opacity-60`}
          >
            <FaCloudUploadAlt />
            Upload
          </button>
        )}

        {onCheck && (
          <button
            type="button"
            onClick={onCheck}
            disabled={busy}
            className="inline-flex items-center gap-2 rounded-xl border px-4 py-2 hover:bg-gray-50 disabled:opacity-60"
          >
            <FaCheckCircle />
            Check
          </button>
        )}

        {onSave && (
          <button
            type="button"
            onClick={onSave}
            disabled={busy}
            className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-white ${primary} disabled:opacity-60`}
          >
            <FaSave />
            Save
          </button>
        )}
      </div>
    );
  };

  return (
    <AdminLayoutWrapper>
      <ToastContainer />
      <div className={`${isDark ? "bg-gray-900 text-white" : "bg-gray-50"} min-h-screen`}>
        <HeaderBar title="Bulk Insert Master" />

        <div className="max-w-7xl mx-auto px-4 py-6 grid grid-cols-1 lg:grid-cols-12 gap-4">
          {/* Left tabs */}
          <div className="lg:col-span-4 space-y-3">
            {tabs.map((t) => (
              <TabButton
  key={t.key}
  active={active === t.key}
  onClick={() => setActive(t.key)}
  icon={t.icon}
  title={t.title}
  desc={t.desc}
  isDark={isDark}
/>
            ))}

            {busy && (
              <div className="rounded-2xl border bg-white p-4">
                <div className="text-sm font-medium">Working...</div>
                <div className="mt-2 w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                  <div
                    className="bg-black h-2"
                    style={{ width: `${Math.min(Math.max(progress, 0), 100)}%` }}
                  />
                </div>
                <div className="text-xs text-gray-500 mt-2">{progress}%</div>
              </div>
            )}
          </div>

          {/* Right content */}
          <div className="lg:col-span-8 space-y-4">
            {active === "items" && (
              <SectionCard
                title="Items Bulk"
                right={<ActionButtons onUploadOnly={itemsUpload} onSave={itemsSave} templateName="BulkItems" />}
              >
                <div className="space-y-4">
                  <FilePicker file={itemsFile} setFile={setItemsFile} />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="rounded-xl border p-4">
                      <div className="font-medium">Save Type</div>
                      <div className="text-sm text-gray-500 mt-1">
                        Run in order: Category → SubCategory → Inner → DeepInner → Item
                      </div>
                      <select
                        value={itemSaveType}
                        onChange={(e) => setItemSaveType(e.target.value)}
                        className="mt-3 w-full rounded-xl border px-3 py-2"
                      >
                        <option value="Category">Category</option>
                        <option value="SubCategory">SubCategory</option>
                        <option value="InnerSubCategory">InnerSubCategory</option>
                        <option value="DeepInnerSubCategory">DeepInnerSubCategory</option>
                        <option value="Item">Item</option>
                      </select>
                    </div>

                    <div className="rounded-xl border p-4 bg-gray-50">
                      <div className="font-medium">Tip</div>
                      <div className="text-sm text-gray-600 mt-2">
                        1) Upload once <br />
                        2) Save Category <br />
                        3) Save SubCategory <br />
                        4) Save Inner <br />
                        5) Save DeepInner <br />
                        6) Save Item
                      </div>
                    </div>
                  </div>
                </div>
              </SectionCard>
            )}

            {active === "supplier" && (
              <SectionCard
                title="Supplier Bulk"
                right={<ActionButtons onCheck={supplierCheck} onSave={supplierSave} templateName="BulkSupplier" />}
              >
                <div className="space-y-4">
                  <FilePicker file={supplierFile} setFile={setSupplierFile} accept=".xls,.xlsx" />
                  <div className="text-sm text-gray-600">
                    First click <b>Check</b>, then <b>Save</b>.
                  </div>
                </div>
              </SectionCard>
            )}

            {active === "vehicle" && (
              <SectionCard
                title="Vehicle Bulk"
                right={
                  <ActionButtons
                    onCheck={vehicleCheck}
                    onSave={vehicleSave}
                    templateName={vehicleMode === "New" ? "BulkVehicle" : "BulkHireVehicle"}
                  />
                }
              >
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="rounded-xl border p-4">
                      <div className="font-medium">Mode</div>
                      <select
                        value={vehicleMode}
                        onChange={(e) => setVehicleMode(e.target.value)}
                        className="mt-3 w-full rounded-xl border px-3 py-2"
                      >
                        <option value="New">New (Own Vehicle)</option>
                        <option value="Hire">Hire Vehicle</option>
                      </select>
                      <div className="text-xs text-gray-500 mt-2">
                        Choose correct template based on mode.
                      </div>
                    </div>

                    <div className="rounded-xl border p-4 bg-gray-50">
                      <div className="font-medium">UserId</div>
                      <div className="text-sm text-gray-600 mt-2">
                        Using: <b>{userId}</b> (from localStorage: AdminID/adminId)
                      </div>
                    </div>
                  </div>

                  <FilePicker file={vehicleFile} setFile={setVehicleFile} />
                </div>
              </SectionCard>
            )}

            {active === "tyre" && (
              <SectionCard
                title="Vehicle Tyre Bulk"
                right={
                  <ActionButtons
                    onCheck={tyreCheck}
                    onSave={tyreSave}
                    templateName={tyreMode === "NewTyre" ? "BulkVehicleNewTyre" : "BulkVehicleRetrededTyre"}
                  />
                }
              >
                <div className="space-y-4">
                  <div className="rounded-xl border p-4">
                    <div className="font-medium">Tyre Mode</div>
                    <select
                      value={tyreMode}
                      onChange={(e) => setTyreMode(e.target.value)}
                      className="mt-3 w-full rounded-xl border px-3 py-2"
                    >
                      <option value="NewTyre">New Tyre</option>
                      <option value="Retreded">Retreaded Tyre</option>
                    </select>
                  </div>
                  <FilePicker file={tyreFile} setFile={setTyreFile} />
                </div>
              </SectionCard>
            )}

            {active === "battery" && (
              <SectionCard
                title="Vehicle Battery Bulk"
                right={<ActionButtons onCheck={batteryCheck} onSave={batterySave} templateName="BulkVehicleBattery" />}
              >
                <div className="space-y-4">
                  <FilePicker file={batteryFile} setFile={setBatteryFile} accept=".xls,.xlsx" />
                </div>
              </SectionCard>
            )}

            {active === "asset" && (
              <SectionCard
                title="Asset Bulk"
                right={
                  <ActionButtons
                    onCheck={assetCheck}
                    onSave={assetSave}
                    templateName={
                      assetType === "Others"
                        ? "BulkAsset"
                        : assetType === "Land"
                        ? "BulkAssetLand"
                        : "BulkAssetSM"
                    }
                  />
                }
              >
                <div className="space-y-4">
                  <div className="rounded-xl border p-4">
                    <div className="font-medium">Asset Type</div>
                    <select
                      value={assetType}
                      onChange={(e) => setAssetType(e.target.value)}
                      className="mt-3 w-full rounded-xl border px-3 py-2"
                    >
                      <option value="Others">Others</option>
                      <option value="Land">Land</option>
                      <option value="SMaterials">SMaterials</option>
                    </select>
                  </div>

                  <FilePicker file={assetFile} setFile={setAssetFile} />
                </div>
              </SectionCard>
            )}

            {active === "payroll" && (
              <SectionCard
                title="Payroll Employee Bulk"
                right={<ActionButtons onCheck={payrollCheck} onSave={payrollSave} templateName="BulkEmployee" />}
              >
                <div className="space-y-4">
                  <div className="rounded-xl border p-4">
                    <div className="font-medium">Financial Year</div>
                    <div className="text-sm text-gray-500 mt-1">Example: 2025-2026</div>
                    <input
                      value={financialYear}
                      onChange={(e) => setFinancialYear(e.target.value)}
                      placeholder="YYYY-YYYY"
                      className="mt-3 w-full rounded-xl border px-3 py-2"
                    />
                  </div>

                  <FilePicker file={payrollFile} setFile={setPayrollFile} />

                  <div className="text-sm text-gray-600">
                    This file must contain <b>Sheet1, Sheet2, Sheet3, Sheet4</b> like ASPX.
                  </div>
                </div>
              </SectionCard>
            )}
          </div>
        </div>
      </div>
    </AdminLayoutWrapper>
  );
};

export default BulkInsertMaster;
